import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Components/Sidebar/Sidebar';
import API_BASE_URL from './config/api';
import Header from './Components/Header/BlueHeader';
import Footer from './Components/Footer/Footer';
import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import { Spinner, Form, Table, Button } from 'react-bootstrap';
import { FaRegEdit, FaBuilding } from 'react-icons/fa';
import { Spinner as RBSpinner } from 'react-bootstrap';

function CorporateProposals() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState('proposals');

  // Define tabs outside of loading conditions
  const corporateTabs = [
    { key: 'hudson', label: 'Hudson', icon: <FaBuilding /> },
    { key: 'bmi', label: 'BMI', icon: <FaBuilding /> },
    { key: 'proposals', label: 'Proposals', icon: <FaRegEdit /> },
  ];

  // Auth token
  const token = localStorage.getItem('managerToken');

  // Data states
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatingProposalId, setGeneratingProposalId] = useState(null);

  // Sidebar handlers
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const handleNavigate = (key) => {
    setActiveTab(key);
    if (key === 'hudson') navigate('/corporate');
    if (key === 'proposals') navigate('/corporate/proposals');
    if (key === 'bmi') navigate('/bmi');
  };

  // Fetch buildings on mount
  useEffect(() => {
    async function fetchBuildings() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/corporateBuildings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch buildings');
        const data = await res.json();
        console.log('corporateBuildings response:', data);
        setBuildings(data);
      } catch (err) {
        setError('Error loading buildings: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchBuildings();
  }, [token]);

  // Fetch units with issues when building changes
  useEffect(() => {
    async function fetchUnits() {
      if (!selectedBuilding) {
        setUnits([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        // Debug: log the buildingId being sent
        console.log('Fetching units for buildingId:', selectedBuilding);
        // Use the new route to get all units for the building
        const res = await fetch(`${API_BASE_URL}/api/getUnitsForBuilding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ buildingId: String(selectedBuilding) })
        });
        if (!res.ok) throw new Error('Failed to fetch unit data');
        const data = await res.json();
        // Only units with issues
        const unitsWithIssues = (data.units || []).filter(unit => unit.inspection !== 'OK' && unit.inspection !== 'Resolved');
        setUnits(unitsWithIssues);
      } catch (err) {
        setError('Error loading units: ' + err.message);
        setUnits([]);
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchUnits();
  }, [selectedBuilding, token]);

  // Generate proposal handler
  const handleGenerateProposal = async (objectId, unitNumber) => {
    setGeneratingProposalId(objectId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate_proposal_pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/pdf', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ objectId })
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proposal_Unit${unitNumber}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      alert('Error generating Proposal: ' + err.message);
    } finally {
      setGeneratingProposalId(null);
    }
  };

  // Filter units by search term
  const filteredUnits = units.filter(u =>
    u.unit_number?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: 'flex' }}>
      <div className="dashboard-sidebar-host"
        onMouseEnter={() => setSidebarCollapsed(false)}
        onMouseLeave={() => setSidebarCollapsed(true)}
        style={{ height: '100vh' }}
      >
        <Sidebar
          activeKey={activeTab}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={() => {}}
          tabs={corporateTabs}
        />
      </div>
      <div className="dashboard-content" style={{ flex: 1, marginLeft: sidebarCollapsed ? '72px' : '240px', transition: 'margin-left 240ms' }}>
        <DashboardNavbar buildingName="Hudson Overview" contractLabel="Hudson Overview" />
        <Header isCorporate />
        <div className="header-wrapper" style={{ width: '94%', marginTop: '2rem' }}>
          <div style={{ padding: '2rem' }}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group controlId="buildingSelect">
                  <Form.Label>Select Building:</Form.Label>
                  <Form.Select
                    value={selectedBuilding}
                    onChange={e => setSelectedBuilding(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Choose a building...</option>
                    {buildings.map(b => (
                      <option key={b.buildingId} value={b.buildingId}>{b.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="searchUnit">
                  <Form.Label>Search Unit:</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter unit number"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
            </div>
            {loading && <Spinner animation="border" role="status" className="mb-3" />} 
            {error && <div className="alert alert-danger mb-3">{error}</div>}
            <div className="table-responsive">
              <Table striped hover>
                <thead className="table-light">
                  <tr>
                    <th>Unit</th>
                    <th>Issue Summary</th>
                    <th>Inspection Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUnits.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center">
                        No units with issues found.
                      </td>
                    </tr>
                  ) : (
                    filteredUnits.map(u => (
                      <tr key={u.objectId}>
                        <td>{u.unit_number}</td>
                        <td>{u.other_notes || 'No notes'}</td>
                        <td>{u.inspection_date || 'N/A'}</td>
                        <td>{u.recommendation}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleGenerateProposal(u.objectId, u.unit_number)}
                            disabled={generatingProposalId === u.objectId}
                          >
                            {generatingProposalId === u.objectId ? (
                              <RBSpinner as="span" animation="border" size="sm" />
                            ) : (
                              "Generate Proposal"
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default CorporateProposals; 